/**
 * Center preview / player for the editor V2 rewrite (task 5).
 *
 * Ported from the `VideoEditor.tsx` monolith's preview region (single
 * `<video>` + absolutely-positioned overlay layers + bottom control bar),
 * re-skinned in the dark V2 theme and wired to the Zustand store instead of
 * the monolith's local `useState` setters.
 *
 * Playback model (differs from the monolith): the store's `currentTime` is
 * the source of truth. `useEditorPlayback` advances it via rAF/wall-clock;
 * this component makes the `<video>` element a *follower* — it seeks the
 * element to the correct in-clip offset whenever `currentTime` / the active
 * clip changes and mirrors `isPlaying` onto the element. Anti-loop guard: we
 * only re-seek when the drift between `video.currentTime` and the target
 * exceeds a threshold (loose while playing so natural decode isn't fought,
 * tight while paused for precise scrubbing), and we never write the video's
 * `timeupdate` back into the store (that would fight the rAF loop).
 */
'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  Loader2,
  Maximize2,
  Pause,
  Play,
  RotateCcw,
  RotateCw,
  Upload,
  Volume2,
  VolumeX,
} from 'lucide-react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { fitAspectRatioInBounds } from '@/lib/editor/editorClips';
import {
  selectActiveClip,
  selectDuration,
  useEditorStore,
  type EditorState,
} from '@/lib/editor/editorStore';
import type {
  EditorAspectRatio,
  EditorOverlayLayer,
  EditorTimelineClip,
  TransitionType,
} from '@/types/editor';
import {
  effectiveTransitionDuration,
  renderStartTimes,
} from '@/lib/editor/transitions';
import { getLayerFontFamily } from '@/lib/editor/fonts';
import { getFilter } from '@/lib/editor/filters';
import { EDITOR_RENDER_CANVAS } from '@/lib/editor/render-contract';
import { EditorEffectVisual } from './EditorEffectVisual';
import { effectEdgeOpacity } from '@/lib/editor/effects';
import { getLayerTransitionVisual } from '@/lib/editor/layerTransitions';
import { layerVisualAt } from '@/lib/editor/layerKeyframes';
import {
  clipFit,
  clipMediaTime,
  clipOpacity,
  clipPlaybackRate,
  clipPositionX,
  clipPositionY,
  clipRotation,
  clipScale,
} from '@/lib/editor/clipProperties';
import { cn } from '@/lib/utils/cn';

// ---------------------------------------------------------------------------
// Pure helpers ported verbatim from VideoEditor.tsx
// ---------------------------------------------------------------------------

function clampTime(value: number, duration: number) {
  return Math.min(Math.max(value, 0), Math.max(duration, 0));
}

function safeClipDuration(value?: number) {
  return Number.isFinite(value) && value && value > 0 ? value : 6;
}

function clipLocalTime(clip: EditorTimelineClip, globalTime: number, renderStart: number) {
  return clampTime(globalTime - renderStart, safeClipDuration(clip.duration));
}

/** In-media playback offset for a clip at a given global timeline time. */
function clipVideoTime(clip: EditorTimelineClip, globalTime: number, renderStart: number) {
  return clipMediaTime(clip, clipLocalTime(clip, globalTime, renderStart));
}

function getClipVisualStyle(
  clip: EditorTimelineClip | null | undefined,
  transitionStyle: CSSProperties = {},
): CSSProperties {
  if (!clip) return transitionStyle;
  const transform = [
    transitionStyle.transform,
    `translate3d(${clipPositionX(clip) - 50}%, ${clipPositionY(clip) - 50}%, 0)`,
    `rotate(${clipRotation(clip)}deg)`,
    `scale(${clipScale(clip)})`,
  ].filter(Boolean).join(' ');
  const transitionOpacity = typeof transitionStyle.opacity === 'number' ? transitionStyle.opacity : 1;
  return {
    ...transitionStyle,
    objectFit: clipFit(clip),
    objectPosition: 'center center',
    transform,
    transformOrigin: 'center center',
    opacity: transitionOpacity * clipOpacity(clip),
  };
}

function formatTimecode(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00.0';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const tenth = Math.floor((seconds % 1) * 10);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${tenth}`;
}

function isImageLayer(layer: EditorOverlayLayer) {
  return (layer.type === 'sticker' || layer.type === 'asset') && Boolean(layer.src);
}

function editableBadgeStyle(layer: EditorOverlayLayer, previewScale: number): CSSProperties {
  if (layer.type !== 'sticker' || layer.src) return {};
  const fontPx = Math.max(layer.size * previewScale, 1);
  const shape = layer.badgeStyle ?? 'rounded';
  const radius =
    shape === 'pill'
      ? 999
      : shape === 'soft-square'
        ? fontPx * 0.18
        : shape === 'outline'
          ? fontPx * 0.24
          : fontPx * 0.34;
  const paddingX = fontPx * (shape === 'pill' ? 0.78 : shape === 'soft-square' ? 0.62 : 0.7);
  const paddingY = fontPx * (shape === 'soft-square' ? 0.32 : 0.36);
  return {
    background: layer.badgeAccentColor
      ? `linear-gradient(135deg, ${layer.backgroundColor} 0%, ${layer.badgeAccentColor} 100%)`
      : layer.backgroundColor,
    borderColor: layer.badgeBorderColor ?? 'transparent',
    borderStyle: 'solid',
    borderWidth: `${Math.max(0, (layer.badgeBorderWidth ?? 0) * previewScale)}px`,
    borderRadius: `${radius}px`,
    padding: `${paddingY}px ${paddingX}px`,
    boxShadow: '0 12px 30px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.18)',
  };
}

// ---------------------------------------------------------------------------
// Transition preview approximation (task 5, v1)
//
// The REAL crossfade is rendered server-side by ffmpeg's xfade filter; this is
// only a lightweight CSS/DOM hint shown while scrubbing/playing in the editor.
// Approach: a second, fully isolated `<video>` (own key/lifecycle, never
// touches `videoRef`/the follower effects above) frozen on clip B's first
// frame is layered over the stage only while `currentTime` is inside the cut
// window, mixed by transition type. It is purely additive and renders nothing
// when there is no transitionIn in range, so existing playback is unaffected.
// ---------------------------------------------------------------------------

/** Transition families used by the live browser preview. */
const SLIDE_TRANSITIONS = new Set<TransitionType>(['slideleft', 'slideright', 'slideup', 'slidedown']);
const WIPE_TRANSITIONS = new Set<TransitionType>(['wipeleft', 'wiperight']);
const SMOOTH_TRANSITIONS = new Set<TransitionType>(['smoothleft', 'smoothright']);
const TRANSITION_HANDOFF_SETTLE_SEC = 0.24;

interface TransitionWindow {
  clipBId: string;
  clipBSrc: string;
  clipBMediaOffset: number;
  clipBPlaybackRate: number;
  clipBFilterId?: string;
  type: TransitionType;
  windowStart: number;
  windowEnd: number;
}

interface ActiveTransition extends TransitionWindow {
  /** Progress across the window, 0 at windowStart, 1 at windowEnd. */
  p: number;
}

/**
 * Per-type CSS mix for the incoming video, driven by progress `p` (0..1).
 * Slides intentionally travel only a short distance and crossfade. Moving a
 * full 100% stage produced an ugly 50/50 split-screen halfway through the
 * transition (especially obvious with portrait clips). The restrained motion
 * keeps the effect readable while the incoming frame always covers the stage.
 */
function getTransitionOverlayStyle(type: TransitionType, p: number): CSSProperties {
  const clamped = Math.min(1, Math.max(0, p));
  const eased = clamped * clamped * (3 - 2 * clamped);
  if (SLIDE_TRANSITIONS.has(type)) {
    const offset = 100 * (1 - eased);
    if (type === 'slideleft') return { transform: `translate3d(${offset}%, 0, 0)` };
    if (type === 'slideright') return { transform: `translate3d(${-offset}%, 0, 0)` };
    if (type === 'slideup') return { transform: `translate3d(0, ${offset}%, 0)` };
    return { transform: `translate3d(0, ${-offset}%, 0)` };
  }
  if (WIPE_TRANSITIONS.has(type)) {
    const hiddenPercent = 100 - 100 * eased;
    // Direction names describe the moving edge: left reveals B from the
    // right, right reveals B from the left (matching FFmpeg xfade naming).
    if (type === 'wipeleft') return { clipPath: `inset(0 0 0 ${hiddenPercent}% round 2px)` };
    return { clipPath: `inset(0 ${hiddenPercent}% 0 0 round 2px)` };
  }
  if (type === 'circleopen') {
    return { clipPath: `circle(${eased * 72}% at 50% 50%)` };
  }
  if (type === 'radial') {
    const angle = Math.max(0.01, eased * 360);
    return {
      WebkitMaskImage: `conic-gradient(from -90deg at 50% 50%, #000 0deg ${angle}deg, transparent ${angle}deg 360deg)`,
      maskImage: `conic-gradient(from -90deg at 50% 50%, #000 0deg ${angle}deg, transparent ${angle}deg 360deg)`,
    };
  }
  if (SMOOTH_TRANSITIONS.has(type)) {
    const edge = type === 'smoothleft' ? 100 - eased * 112 : eased * 112 - 12;
    const start = Math.max(0, Math.min(100, edge));
    const end = Math.max(0, Math.min(100, edge + 12));
    const mask =
      type === 'smoothleft'
        ? `linear-gradient(to right, transparent 0% ${start}%, #000 ${end}% 100%)`
        : `linear-gradient(to right, #000 0% ${start}%, transparent ${end}% 100%)`;
    return { WebkitMaskImage: mask, maskImage: mask };
  }
  if (type === 'dissolve') {
    const delays = [0.02, 0.28, 0.12, 0.36, 0.08, 0.31, 0.18, 0.39, 0.05, 0.23];
    const masks = delays.map((delay, index) => {
      const phase = Math.min(1, Math.max(0, (eased - delay) / (1 - delay)));
      const stop = phase * 100;
      const direction = index % 2 === 0 ? 'to bottom' : 'to top';
      return `linear-gradient(${direction}, #000 0% ${stop}%, transparent ${Math.min(
        100,
        stop + 1
      )}% 100%)`;
    });
    const positions = delays.map((_, index) => `${(index / (delays.length - 1)) * 100}% 0`);
    return {
      WebkitMaskImage: masks.join(','),
      maskImage: masks.join(','),
      WebkitMaskSize: `${100 / delays.length}% 100%`,
      maskSize: `${100 / delays.length}% 100%`,
      WebkitMaskPosition: positions.join(','),
      maskPosition: positions.join(','),
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
    };
  }
  // fade
  return { opacity: eased };
}

/** A subtle outgoing-frame parallax makes directional transitions feel like
 * one continuous camera move without exposing two half-width videos. */
function getTransitionBaseStyle(type: TransitionType, p: number): CSSProperties {
  const clamped = Math.min(1, Math.max(0, p));
  const eased = clamped * clamped * (3 - 2 * clamped);
  if (SLIDE_TRANSITIONS.has(type)) {
    const offset = 100 * eased;
    if (type === 'slideleft') return { transform: `translate3d(${-offset}%, 0, 0)` };
    if (type === 'slideright') return { transform: `translate3d(${offset}%, 0, 0)` };
    if (type === 'slideup') return { transform: `translate3d(0, ${-offset}%, 0)` };
    return { transform: `translate3d(0, ${offset}%, 0)` };
  }
  if (type === 'fade' || type === 'dissolve') {
    return { opacity: 1 - eased };
  }
  return {};
}

// Canva-style corner handles only (no edge bars — they covered small text).
type ResizeMode = 'nw' | 'ne' | 'sw' | 'se';

/** Drag-direction signs per corner: which way the pointer grows the box. */
const CORNER_SIGN: Record<ResizeMode, { sx: number; sy: number }> = {
  nw: { sx: -1, sy: -1 },
  ne: { sx: 1, sy: -1 },
  sw: { sx: -1, sy: 1 },
  se: { sx: 1, sy: 1 },
};

type LayerResizeState = {
  id: string;
  mode: ResizeMode;
  startX: number;
  startY: number;
  startSize: number;
  startWidthPercent: number;
};

const ASPECT_OPTIONS: EditorAspectRatio[] = ['9:16', '16:9', '1:1'];

export interface EditorPreviewProps {
  /** Optional project title for the "Плеер" header subtitle. */
  title?: string;
}

/**
 * Self-contained center player. Reads clips/layers/playback/selection off the
 * store and drives the `<video>` element + overlay layers. All mutations go
 * through store actions (`setCurrentTime`, `setIsPlaying`, `updateLayer`,
 * `selectLayer`).
 */
export function EditorPreview({ title }: EditorPreviewProps) {
  // ---- store reads
  const clips = useEditorStore((s) => s.clips);
  const layers = useEditorStore((s) => s.layers);
  const currentTime = useEditorStore((s) => s.currentTime);
  const isPlaying = useEditorStore((s) => s.isPlaying);
  const aspectRatio = useEditorStore((s) => s.aspectRatio);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const trackSettings = useEditorStore((s) => s.trackSettings);

  // ---- store actions
  const setCurrentTime = useEditorStore((s) => s.setCurrentTime);
  const setIsPlaying = useEditorStore((s) => s.setIsPlaying);
  const updateLayer = useEditorStore((s) => s.updateLayer);
  const selectLayer = useEditorStore((s) => s.selectLayer);
  const setAspectRatio = useEditorStore((s) => s.setAspectRatio);

  const duration = useEditorStore((s) => selectDuration(s));
  const activeClip = useEditorStore((s) => selectActiveClip(s, s.currentTime));
  const audioTracks = useEditorStore((s) => s.audioTracks);

  // ---- refs / local interaction state
  const stageRef = useRef<HTMLDivElement | null>(null);
  const stageViewportRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const transitionVideoRef = useRef<HTMLVideoElement | null>(null);
  const mainFrameRequestRef = useRef(0);
  const mainReadyCommittedClipRef = useRef<string | null>(null);
  const bgAudioRefs = useRef(new Map<string, HTMLAudioElement>());
  const [videoReady, setVideoReady] = useState(false);
  const [mainVideoReadyClipId, setMainVideoReadyClipId] = useState<string | null>(null);
  const [mainVideoReadyAt, setMainVideoReadyAt] = useState<{
    clipId: string;
    timelineTime: number;
  } | null>(null);
  const [transitionFrameReadyClipId, setTransitionFrameReadyClipId] = useState<string | null>(null);
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  const [draggingLayerId, setDraggingLayerId] = useState<string | null>(null);
  const [resizingLayer, setResizingLayer] = useState<LayerResizeState | null>(null);
  const [rotatingLayerId, setRotatingLayerId] = useState<string | null>(null);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  // Canva-style double-click-to-edit for on-canvas text/sticker layers. The
  // editable node itself is UNCONTROLLED (see the ref callback below) so
  // typing never fights the caret; the store is the eventual source of truth
  // via `onInput`, and `preEditTextRef` snapshots the pre-edit text so Escape
  // can revert it exactly.
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  // Measured stage pixel size → lets us render text at the SAME proportion it
  // will occupy in the render canvas (WYSIWYG): a `layer.size` given in render
  // px (canvas is 1080×1920 etc.) is scaled by stageHeight / canvasHeight so
  // the preview matches the exported video instead of drawing raw px in the
  // small player box.
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const [stageViewportSize, setStageViewportSize] = useState({ w: 0, h: 0 });
  const preEditTextRef = useRef<string>('');

  const activeClipId = activeClip?.id ?? null;
  const activeClipSrc = activeClip?.src ?? null;
  const isActiveMainVideoReady = Boolean(
    videoReady && activeClipId && mainVideoReadyClipId === activeClipId
  );
  const clipRenderStarts = useMemo(() => renderStartTimes(clips), [clips]);
  const activeClipRenderStart = activeClip
    ? clipRenderStarts[clips.findIndex((clip) => clip.id === activeClip.id)] ?? activeClip.start
    : 0;

  // -----------------------------------------------------------------------
  // Transition preview approximation (additive — see helpers above; does not
  // read/write `videoRef` or any state consumed by the follower effects).
  // -----------------------------------------------------------------------

  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(query.matches);
    const handleChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  // Static per-clip cut windows; only recomputed when the clip list changes.
  const transitionWindows = useMemo<TransitionWindow[]>(() => {
    const windows: TransitionWindow[] = [];
    for (let index = 1; index < clips.length; index++) {
      const clipB = clips[index];
      const transitionIn = clipB.transitionIn;
      if (!transitionIn) continue;
      const clipA = clips[index - 1];
      const effectiveDuration = effectiveTransitionDuration(
        safeClipDuration(clipA.duration),
        safeClipDuration(clipB.duration),
        transitionIn
      );
      const windowStart = clipRenderStarts[index];
      windows.push({
        clipBId: clipB.id,
        clipBSrc: clipB.src,
        clipBMediaOffset: clipB.mediaOffset,
        clipBPlaybackRate: clipPlaybackRate(clipB),
        clipBFilterId: clipB.filterId,
        type: transitionIn.type,
        windowStart,
        windowEnd: windowStart + effectiveDuration,
      });
    }
    return windows;
  }, [clips, clipRenderStarts]);

  // Which window (if any) `currentTime` currently falls inside, plus progress.
  const activeTransition = useMemo<ActiveTransition | null>(() => {
    const win = transitionWindows.find(
      (w) => currentTime >= w.windowStart && currentTime <= w.windowEnd
    );
    if (!win) return null;
    const span = Math.max(win.windowEnd - win.windowStart, 0.0001);
    const p = Math.min(1, Math.max(0, (currentTime - win.windowStart) / span));
    return { ...win, p };
  }, [transitionWindows, currentTime]);

  // Keep exactly one auxiliary player warm: the transition currently handing
  // off to clip B, or otherwise the next transition ahead of the playhead.
  // Unlike a short time-based preload window, this begins decoding the next
  // clip as soon as the project opens and remains bounded to one extra video.
  const pendingTransition = useMemo<TransitionWindow | null>(() => {
    const readyAtForActiveClip =
      mainVideoReadyAt?.clipId === activeClipId ? mainVideoReadyAt.timelineTime : null;
    const handoff = transitionWindows.find(
      (w) =>
        w.clipBId === activeClipId &&
        currentTime >= w.windowEnd &&
        (!isActiveMainVideoReady ||
          readyAtForActiveClip === null ||
          currentTime <= readyAtForActiveClip + TRANSITION_HANDOFF_SETTLE_SEC)
    );
    return handoff ?? transitionWindows.find((w) => currentTime <= w.windowEnd) ?? null;
  }, [activeClipId, currentTime, isActiveMainVideoReady, mainVideoReadyAt, transitionWindows]);

  // We're inside a transition. During playback the incoming clip plays live;
  // while paused/scrubbing it seeks to the exact transition frame. The visual
  // mix no longer uses a full-stage slide, so a paused midpoint remains a soft
  // full-frame blend instead of the old broken-looking 50/50 split-screen.
  const inActiveTransition = Boolean(
    activeTransition &&
      pendingTransition &&
      activeTransition.clipBId === pendingTransition.clipBId
  );

  // After the transition reaches 100%, keep the already-decoded auxiliary
  // player visible until the newly-mounted main player for clip B presents a
  // frame. This removes the one-frame/one-GOP freeze at the player handoff.
  const inTransitionHandoff = Boolean(
    pendingTransition &&
      activeClipId === pendingTransition.clipBId &&
      currentTime >= pendingTransition.windowEnd &&
      (!isActiveMainVideoReady ||
        mainVideoReadyAt?.clipId !== activeClipId ||
        currentTime <= mainVideoReadyAt.timelineTime + TRANSITION_HANDOFF_SETTLE_SEC)
  );

  const transitionHandoffProgress =
    inTransitionHandoff &&
    pendingTransition &&
    isActiveMainVideoReady &&
    mainVideoReadyAt?.clipId === activeClipId
      ? Math.min(
          1,
          Math.max(
            0,
            (currentTime - mainVideoReadyAt.timelineTime) / TRANSITION_HANDOFF_SETTLE_SEC
          )
        )
      : 0;

  const transitionFrameReady = Boolean(
    pendingTransition && transitionFrameReadyClipId === pendingTransition.clipBId
  );

  const markMainFrameReady = useCallback(
    (video: HTMLVideoElement, clipId: string) => {
      if (mainReadyCommittedClipRef.current === clipId) return;
      if (
        videoRef.current !== video ||
        video.seeking ||
        video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
      ) {
        return;
      }

      const state = useEditorStore.getState();
      const currentClip = selectActiveClip(state, state.currentTime);
      if (!currentClip || currentClip.id !== clipId) return;
      const index = state.clips.findIndex((clip) => clip.id === clipId);
      const renderStart = renderStartTimes(state.clips)[index] ?? currentClip.start;
      const expectedTime = clipVideoTime(currentClip, state.currentTime, renderStart);
      const warmVideo = transitionVideoRef.current;
      if (
        warmVideo &&
        warmVideo.currentSrc &&
        warmVideo.currentSrc === video.currentSrc &&
        warmVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
        Math.abs((video.currentTime || 0) - (warmVideo.currentTime || 0)) > 0.08
      ) {
        try {
          video.currentTime = warmVideo.currentTime;
        } catch {
          // A seeked/playing event retries readiness once the exact warm frame lands.
        }
        return;
      }
      if (Math.abs((video.currentTime || 0) - expectedTime) > 0.2) return;

      const requestId = ++mainFrameRequestRef.current;
      let committed = false;
      const commitAfterPaint = () => {
        if (committed) return;
        committed = true;
        window.requestAnimationFrame(() => {
          if (requestId !== mainFrameRequestRef.current || videoRef.current !== video) return;
          const paintedWarmVideo = transitionVideoRef.current;
          if (
            paintedWarmVideo &&
            paintedWarmVideo.currentSrc === video.currentSrc &&
            Math.abs((video.currentTime || 0) - (paintedWarmVideo.currentTime || 0)) > 0.08
          ) {
            try {
              video.currentTime = paintedWarmVideo.currentTime;
            } catch {
              // The resulting seeked event retries the compositor commit.
            }
            return;
          }
          mainReadyCommittedClipRef.current = clipId;
          setVideoReady(true);
          setMainVideoReadyClipId(clipId);
          setMainVideoReadyAt({
            clipId,
            timelineTime: useEditorStore.getState().currentTime,
          });
          setPlaybackError(null);
        });
      };

      if (typeof video.requestVideoFrameCallback === 'function') {
        // rVFC confirms that a decoded frame was submitted to the compositor;
        // the extra rAF keeps the warm transition overlay for that paint and
        // removes it only on the following one, preventing a black flash.
        video.requestVideoFrameCallback(() => commitAfterPaint());
      } else {
        // Two paints provide the same conservative handoff on older browsers.
        window.requestAnimationFrame(commitAfterPaint);
      }

      // Chrome may withhold rVFC for an already-decoded video that is paused.
      // This fallback never runs on metadata alone: it re-checks that the
      // exact element still owns a decoded, non-seeking current frame.
      window.setTimeout(() => {
        if (
          requestId !== mainFrameRequestRef.current ||
          videoRef.current !== video ||
          video.seeking ||
          video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA
        ) {
          return;
        }
        commitAfterPaint();
      }, 220);
    },
    []
  );

  const markTransitionFrameReady = useCallback(
    (video: HTMLVideoElement, clipId: string) => {
      let committed = false;
      const commit = () => {
        if (committed || transitionVideoRef.current !== video) return;
        committed = true;
        setTransitionFrameReadyClipId(clipId);
      };

      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(() => commit());
        // Defensive fallback for browsers that expose rVFC but do not deliver
        // it for a paused, freshly-seeked frame until the next compositor tick.
        window.setTimeout(commit, 180);
      } else {
        window.requestAnimationFrame(commit);
      }
    },
    []
  );

  useEffect(() => {
    setTransitionFrameReadyClipId(null);
    const video = transitionVideoRef.current;
    if (!video || !pendingTransition) return;
    // The src+preload mount normally starts fetching by itself. Calling
    // load() again while that request is in flight resets resource selection
    // and can make the first transition lose the race it was meant to avoid.
    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
    // Depend on the incoming-clip id, NOT the whole object — `pendingTransition`
    // gets a new identity on ANY clip edit (store mutations do clips.map), which
    // would force-reset ready-state and collapse the crossfade to a hard cut
    // when an unrelated clip is edited mid-transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTransition?.clipBId]);

  // Drive the isolated overlay <video>: play it live while inside the cut,
  // otherwise pause and rewind it to the incoming clip's head so it's ready.
  useEffect(() => {
    const v = transitionVideoRef.current;
    if (!v) return;
    v.playbackRate = pendingTransition?.clipBPlaybackRate ?? 1;
    if ((inActiveTransition || inTransitionHandoff) && isPlaying) {
      v.play().catch(() => {});
    } else {
      v.pause();
      if (!inActiveTransition && !inTransitionHandoff) {
        const target = pendingTransition?.clipBMediaOffset ?? 0;
        if (Math.abs((v.currentTime || 0) - target) > 0.03) {
          setTransitionFrameReadyClipId(null);
          try {
            v.currentTime = target;
          } catch {
            // metadata not ready yet; the onLoadedMetadata seek handles it.
          }
        }
      }
    }
  }, [inActiveTransition, inTransitionHandoff, isPlaying, pendingTransition?.clipBMediaOffset, pendingTransition?.clipBPlaybackRate]);

  useEffect(() => {
    const v = transitionVideoRef.current;
    if (!v || !pendingTransition || (!inActiveTransition && !inTransitionHandoff)) return;
    const target =
      pendingTransition.clipBMediaOffset +
      Math.max(0, currentTime - pendingTransition.windowStart) * pendingTransition.clipBPlaybackRate;
    const driftLimit = inTransitionHandoff ? 0.08 : isPlaying ? 0.14 : 0.05;
    if (Math.abs((v.currentTime || 0) - target) > driftLimit) {
      setTransitionFrameReadyClipId(null);
      try {
        v.currentTime = target;
      } catch {
        // metadata is not ready yet; the loadedmetadata handler retries.
      }
    }
  }, [currentTime, inActiveTransition, inTransitionHandoff, isPlaying, pendingTransition]);

  // ---- Multi-track audio preview. Every track owns an independent media
  // clock, timeline window, source offset, fades and ducking amount.
  useEffect(() => {
    const driftLimit = isPlaying ? 0.35 : 0.08;
    const trackGain = trackSettings.music.muted || trackSettings.music.hidden ? 0 : 1;
    const hasSoloTrack = audioTracks.some((track) => track.solo);
    audioTracks.forEach((track) => {
      const id = track.id;
      if (!id) return;
      const audio = bgAudioRefs.current.get(id);
      if (!audio) return;
      const start = Math.max(0, track.start ?? 0);
      const trackDuration = Math.max(0.1, track.timelineDuration ?? track.duration ?? duration);
      const localTime = currentTime - start;
      const active = localTime >= 0 && localTime <= trackDuration;
      if (!active) {
        audio.pause();
        return;
      }
      const target = Math.max(0, track.sourceOffset ?? 0) + localTime;
      if (Math.abs((audio.currentTime || 0) - target) > driftLimit) {
        try {
          audio.currentTime = target;
        } catch {
          // Metadata can arrive after the playhead; the next effect pass retries.
        }
      }
      const fadeIn = Math.min(track.fadeIn ?? 0, trackDuration / 2);
      const fadeOut = Math.min(track.fadeOut ?? 0, trackDuration / 2);
      const fadeInGain = fadeIn > 0 ? Math.min(1, localTime / fadeIn) : 1;
      const fadeOutGain = fadeOut > 0 ? Math.min(1, (trackDuration - localTime) / fadeOut) : 1;
      const duckGain = track.ducking && (activeClip?.volume ?? 1) > 0 ? 0.32 : 1;
      const mixerGain = track.muted || (hasSoloTrack && !track.solo) ? 0 : 1;
      audio.volume = Math.min(
        1,
        Math.max(0, (track.volume ?? 0.5) * fadeInGain * fadeOutGain * duckGain * mixerGain * trackGain * (muted ? 0 : 1))
      );
      if (isPlaying) audio.play().catch(() => {});
      else audio.pause();
    });
  }, [activeClip?.volume, audioTracks, currentTime, duration, isPlaying, muted, trackSettings.music]);

  // -----------------------------------------------------------------------
  // Video ↔ store sync
  // -----------------------------------------------------------------------

  // (1) Active clip changed → reset ready state, (re)load the element and seek
  //     to the correct in-clip offset, then play if the store says so.
  useEffect(() => {
    mainFrameRequestRef.current += 1;
    mainReadyCommittedClipRef.current = null;
    setVideoReady(false);
    setMainVideoReadyClipId(null);
    setMainVideoReadyAt(null);
    setPlaybackError(null);
    const video = videoRef.current;
    if (!video || !activeClip) return;
    video.playbackRate = clipPlaybackRate(activeClip);

    const target = clipVideoTime(activeClip, useEditorStore.getState().currentTime, activeClipRenderStart);
    const applyInitialTime = () => {
      try {
        video.currentTime = target;
      } catch {
        // Some freshly-uploaded codecs expose metadata late; the metadata
        // event below retries.
      }
    };

    video.pause();
    if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
    if (video.readyState >= 1) {
      applyInitialTime();
    } else {
      video.addEventListener('loadedmetadata', applyInitialTime, { once: true });
    }

    if (useEditorStore.getState().isPlaying) {
      video.play().catch(() => {
        setPlaybackError('Не удалось воспроизвести видео');
        setIsPlaying(false);
      });
    }

    return () => {
      video.removeEventListener('loadedmetadata', applyInitialTime);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClipId, activeClipSrc, activeClipRenderStart]);

  // (2) currentTime changed → seek the element, but only when drift exceeds a
  //     threshold (anti-loop guard). Loose threshold while playing so we don't
  //     fight the element's own smooth decode; tight while paused for scrubbing.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip || video.readyState < 1) return;
    const target = clipVideoTime(activeClip, currentTime, activeClipRenderStart);
    const threshold = inTransitionHandoff ? 0.08 : isPlaying ? 0.3 : 0.08;
    if (Math.abs((video.currentTime || 0) - target) < threshold) return;
    try {
      video.currentTime = target;
      setPlaybackError(null);
    } catch {
      // Ignore transient seek rejections while metadata is still loading.
    }
  }, [activeClip, activeClipRenderStart, currentTime, inTransitionHandoff, isPlaying]);

  // (3) isPlaying changed → mirror onto the element.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !activeClip) return;
    video.playbackRate = clipPlaybackRate(activeClip);
    if (isPlaying) {
      video.play().catch(() => {
        setPlaybackError('Не удалось воспроизвести видео');
        setIsPlaying(false);
      });
    } else {
      video.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, activeClipId, activeClip?.playbackRate]);

  // (4) volume / mute → element.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const trackGain = trackSettings.video.muted ? 0 : 1;
    video.volume = Math.min(1, Math.max(0, volume * (activeClip?.volume ?? 1) * trackGain));
    video.muted = muted;
  }, [volume, muted, activeClip?.volume, trackSettings.video.muted]);

  // -----------------------------------------------------------------------
  // Playback controls
  // -----------------------------------------------------------------------

  const togglePlayback = useCallback(() => {
    if (!activeClip) return;
    if (!isPlaying) {
      // Restart from the top if the playhead is parked at the very end.
      if (duration > 0 && currentTime >= duration - 0.08) {
        setCurrentTime(0);
      }
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [activeClip, isPlaying, duration, currentTime, setCurrentTime, setIsPlaying]);

  const skipBy = useCallback(
    (deltaSeconds: number) => {
      if (duration <= 0) return;
      seekToTime(clampTime(currentTime + deltaSeconds, duration));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTime, duration]
  );

  // Seek to an absolute global time and keep the store's selected clip in sync
  // so the timeline highlight follows the playhead across clip boundaries.
  const seekToTime = useCallback(
    (nextTime: number) => {
      const clamped = clampTime(nextTime, duration);
      const targetClip =
        clips.find(
          (clip) => clamped >= clip.start && clamped < clip.start + safeClipDuration(clip.duration)
        ) ?? clips[clips.length - 1];
      useEditorStore.setState({
        currentTime: clamped,
        selectedClipId: targetClip?.id ?? null,
      } as Partial<EditorState>);
      setPlaybackError(null);
    },
    [clips, duration]
  );

  const seekToClientX = useCallback(
    (clientX: number, element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      seekToTime(ratio * duration);
    },
    [duration, seekToTime]
  );

  const toggleFullscreen = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void el.requestFullscreen?.();
    }
  }, []);

  // Track the stage's rendered pixel size for WYSIWYG text scaling.
  useEffect(() => {
    const el = stageViewportRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const styles = window.getComputedStyle(el);
      const horizontalPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const verticalPadding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      setStageViewportSize({
        w: Math.max(0, rect.width - horizontalPadding),
        h: Math.max(0, rect.height - verticalPadding),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setStageSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [aspectRatio]);

  // px→preview scale: `layer.size` is authored in render-canvas px, shown here
  // proportionally so the preview matches the export. Falls back to a sane
  // ratio for the first paint before the observer measures.
  const renderCanvasHeight = EDITOR_RENDER_CANVAS[aspectRatio].height;
  const previewScale = stageSize.h > 0 ? stageSize.h / renderCanvasHeight : 0.3;
  const fittedStageSize = useMemo(
    () => fitAspectRatioInBounds(stageViewportSize.w, stageViewportSize.h, aspectRatio),
    [aspectRatio, stageViewportSize.h, stageViewportSize.w]
  );

  // -----------------------------------------------------------------------
  // Layer drag / resize (ported math, wired to updateLayer)
  // -----------------------------------------------------------------------

  const moveLayer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const stage = stageRef.current;
      if (!stage) return;

      if (rotatingLayerId) {
        const rect = stage.getBoundingClientRect();
        const layer = layers.find((l) => l.id === rotatingLayerId);
        if (!layer) return;
        // Layer center in screen px: the overlay div is anchored at
        // (x%, y%) of the stage regardless of its current rotation (a
        // `translate(-50%,-50%) rotate()` composition keeps that point
        // fixed as the visual center — see beginLayerRotate).
        const centerX = rect.left + (layer.x / 100) * rect.width;
        const centerY = rect.top + (layer.y / 100) * rect.height;
        const dx = event.clientX - centerX;
        const dy = event.clientY - centerY;
        // atan2(dx, -dy): 0deg when the pointer is directly above center,
        // growing clockwise — matches CSS `rotate(deg)`'s positive direction.
        let angle = Math.atan2(dx, -dy) * (180 / Math.PI);
        if (angle <= -180) angle += 360;
        if (angle > 180) angle -= 360;
        // Snap to the cardinal angles within ~4° for convenience.
        for (const snapTo of [-180, -90, 0, 90, 180]) {
          if (Math.abs(angle - snapTo) < 4) {
            angle = snapTo === -180 ? 180 : snapTo;
            break;
          }
        }
        updateLayer(rotatingLayerId, { rotation: Math.round(angle * 10) / 10 });
        return;
      }

      if (resizingLayer) {
        const rect = stage.getBoundingClientRect();
        const deltaX = event.clientX - resizingLayer.startX;
        const deltaY = event.clientY - resizingLayer.startY;
        const layer = layers.find((l) => l.id === resizingLayer.id);
        if (!layer) return;

        const { sx, sy } = CORNER_SIGN[resizingLayer.mode];

        if (isImageLayer(layer)) {
          const resizeDelta = sx * deltaX;
          const deltaPercent = (resizeDelta / Math.max(rect.width, 1)) * 100;
          updateLayer(resizingLayer.id, {
            widthPercent: Math.min(90, Math.max(8, resizingLayer.startWidthPercent + deltaPercent)),
          });
          return;
        }

        const signedDelta = (sx * deltaX + sy * deltaY) / 2;
        // Handle tracks the pointer: a screen-px drag maps back to render px
        // through the same previewScale used to draw the text.
        const sizeDelta = signedDelta / Math.max(previewScale, 0.01);
        updateLayer(resizingLayer.id, {
          size: Math.min(600, Math.max(10, Math.round(resizingLayer.startSize + sizeDelta))),
        });
        return;
      }

      if (!draggingLayerId) return;
      const rect = stage.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      updateLayer(draggingLayerId, {
        x: Math.min(94, Math.max(6, x)),
        y: Math.min(92, Math.max(8, y)),
      });
    },
    [rotatingLayerId, resizingLayer, draggingLayerId, layers, updateLayer, previewScale]
  );

  const endInteraction = useCallback(() => {
    setDraggingLayerId(null);
    setResizingLayer(null);
    setRotatingLayerId(null);
  }, []);

  const beginLayerResize = useCallback(
    (layer: EditorOverlayLayer, mode: ResizeMode) => (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      selectLayer(layer.id);
      setDraggingLayerId(null);
      setRotatingLayerId(null);
      setResizingLayer({
        id: layer.id,
        mode,
        startX: event.clientX,
        startY: event.clientY,
        startSize: layer.size,
        startWidthPercent: layer.widthPercent ?? 26,
      });
    },
    [selectLayer]
  );

  /**
   * Canva-style rotate handle: mirrors `beginLayerResize`'s pattern (stage-
   * level `onPointerMove`/`onPointerUp` do the tracking, no per-element
   * pointer capture) but only needs the layer id — `moveLayer` recomputes
   * the on-stage center from the layer's live `x`/`y` on every move, since
   * rotation never changes them.
   */
  const beginLayerRotate = useCallback(
    (layer: EditorOverlayLayer) => (event: ReactPointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      selectLayer(layer.id);
      setDraggingLayerId(null);
      setResizingLayer(null);
      setRotatingLayerId(layer.id);
    },
    [selectLayer]
  );

  const beginLayerDrag = useCallback(
    (layer: EditorOverlayLayer) => (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      selectLayer(layer.id);
      setResizingLayer(null);
      setRotatingLayerId(null);
      setDraggingLayerId(layer.id);
    },
    [selectLayer]
  );

  const visibleLayers = useMemo(
    () => trackSettings.overlays.hidden
      ? []
      : layers
          .filter((layer) => currentTime >= layer.start && currentTime <= layer.end)
          .map((layer) => layerVisualAt(layer, currentTime)),
    [layers, currentTime, trackSettings.overlays.hidden]
  );

  const playedPercent = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;

  const activeFilterCss = getFilter(activeClip?.filterId).css;
  const activeFilterStyle: CSSProperties | undefined = activeFilterCss ? { filter: activeFilterCss } : undefined;
  const incomingFilterCss = getFilter(pendingTransition?.clipBFilterId).css;
  const incomingFilterStyle: CSSProperties | undefined = incomingFilterCss ? { filter: incomingFilterCss } : undefined;
  const incomingClip = pendingTransition
    ? clips.find((clip) => clip.id === pendingTransition.clipBId)
    : null;
  const mainTransitionStyle = inActiveTransition && activeTransition && transitionFrameReady
    ? getTransitionBaseStyle(reducedMotion ? 'fade' : activeTransition.type, activeTransition.p)
    : {};
  const incomingTransitionStyle = inTransitionHandoff && transitionFrameReady
    ? {
        opacity:
          1 -
          transitionHandoffProgress *
            transitionHandoffProgress *
            (3 - 2 * transitionHandoffProgress),
        clipPath: 'none',
        WebkitMaskImage: 'none',
        maskImage: 'none',
      }
    : inActiveTransition && activeTransition && transitionFrameReady
      ? getTransitionOverlayStyle(reducedMotion ? 'fade' : activeTransition.type, activeTransition.p)
      : { opacity: 0 };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#101012]">
      {/* Header */}
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-white/10 px-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-tight text-white">Плеер</p>
          <p className="max-w-[420px] truncate text-xs text-white/40">
            {activeClip?.title ?? title ?? 'Предпросмотр монтажа'}
          </p>
        </div>
        <div className="rounded-full bg-white/5 px-2.5 py-1 text-xs font-semibold tabular-nums text-white/60">
          {formatTimecode(currentTime)} / {formatTimecode(duration)}
        </div>
      </div>

      {/* Stage */}
      <div
        ref={stageViewportRef}
        className="flex min-h-0 flex-1 items-center justify-center bg-[radial-gradient(circle_at_center,rgba(91,53,255,0.09),transparent_50%),#101012] p-3"
      >
        <div
          ref={stageRef}
          onPointerDownCapture={(event) => {
            const target = event.target;
            if (target instanceof HTMLElement && target.closest('[data-layer-overlay]')) return;
            selectLayer(null);
          }}
          onPointerMove={moveLayer}
          onPointerUp={endInteraction}
          onPointerLeave={endInteraction}
          onClick={(event) => {
            // Clicking empty stage (not a layer) just deselects — playback is
            // controlled only from the control bar, never by clicking the video.
            const target = event.target;
            if (target instanceof HTMLElement && target.closest('[data-layer-overlay]')) return;
            selectLayer(null);
          }}
          data-aspect-ratio={aspectRatio}
          className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-[1.35rem] bg-ink-950 shadow-[0_24px_90px_rgba(7,5,12,0.45)]"
          style={
            fittedStageSize.width > 0
              ? { width: fittedStageSize.width, height: fittedStageSize.height }
              : { aspectRatio: aspectRatio.replace(':', ' / '), maxWidth: '100%', maxHeight: '100%' }
          }
        >
          {activeClip ? (
            <video
              data-main-video
              data-main-frame-ready={isActiveMainVideoReady ? 'true' : 'false'}
              key={`main-${activeClip.id}`}
              ref={videoRef}
              src={activeClip.src}
              className="h-full w-full bg-black object-contain"
              style={{
                ...activeFilterStyle,
                ...getClipVisualStyle(activeClip, mainTransitionStyle),
                ...(trackSettings.video.hidden ? { opacity: 0 } : {}),
              }}
              playsInline
              preload="auto"
              onLoadedData={(event) => markMainFrameReady(event.currentTarget, activeClip.id)}
              onCanPlay={(event) => markMainFrameReady(event.currentTarget, activeClip.id)}
              onSeeked={(event) => markMainFrameReady(event.currentTarget, activeClip.id)}
              onPlaying={(event) => markMainFrameReady(event.currentTarget, activeClip.id)}
              onError={(event) => {
                if (event.currentTarget.error?.code === MediaError.MEDIA_ERR_ABORTED) return;
                setVideoReady(false);
                setMainVideoReadyClipId(null);
                setPlaybackError('Не удалось воспроизвести видео');
                setIsPlaying(false);
              }}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-center text-white/70">
              <Upload className="h-10 w-10" strokeWidth={1.6} />
              <p className="max-w-xs text-sm font-semibold">
                Добавьте видео, чтобы начать монтаж
              </p>
            </div>
          )}

          {/* Transition preview. One auxiliary player stays pre-decoded for the
              next transition. It is revealed only after a real frame reaches
              the compositor, and remains visible through the clip-B handoff
              until the new main player has presented its own frame. */}
          {pendingTransition && (
            <video
              data-transition-video
              data-transition-frame-ready={transitionFrameReady ? 'true' : 'false'}
              data-transition-handoff={inTransitionHandoff ? 'true' : 'false'}
              key={`transition-${pendingTransition.clipBId}`}
              ref={transitionVideoRef}
              src={pendingTransition.clipBSrc}
              muted
              playsInline
              preload="auto"
              aria-hidden="true"
              tabIndex={-1}
              className="pointer-events-none absolute inset-0 z-[3] h-full w-full bg-black object-contain"
              style={{
                ...incomingFilterStyle,
                ...getClipVisualStyle(incomingClip, incomingTransitionStyle),
                ...(trackSettings.video.hidden ? { opacity: 0 } : {}),
              }}
              onLoadedMetadata={(event) => {
                try {
                  const target =
                    pendingTransition.clipBMediaOffset +
                    Math.max(0, currentTime - pendingTransition.windowStart) *
                      pendingTransition.clipBPlaybackRate;
                  event.currentTarget.currentTime = target;
                  event.currentTarget.playbackRate = pendingTransition.clipBPlaybackRate;
                } catch {
                  // metadata not ready yet; the play/pause effect re-seeks.
                }
              }}
              onLoadedData={(event) =>
                markTransitionFrameReady(event.currentTarget, pendingTransition.clipBId)
              }
              onCanPlay={(event) =>
                markTransitionFrameReady(event.currentTarget, pendingTransition.clipBId)
              }
              onSeeked={(event) =>
                markTransitionFrameReady(event.currentTarget, pendingTransition.clipBId)
              }
              onPlaying={(event) =>
                markTransitionFrameReady(event.currentTarget, pendingTransition.clipBId)
              }
            />
          )}

          {/* Independent hidden media elements for the multi-track audio timeline. */}
          {audioTracks.map((track) => track.id && track.src ? (
            <audio
              key={track.id}
              ref={(element) => {
                if (element) bgAudioRefs.current.set(track.id!, element);
                else bgAudioRefs.current.delete(track.id!);
              }}
              src={track.src}
              preload="auto"
              className="hidden"
              aria-hidden="true"
            />
          ) : null)}

          {/* Overlay layers */}
          {visibleLayers.map((layer) => {
            const isSelected = selectedLayerId === layer.id;
            const layerMotion = getLayerTransitionVisual(layer, currentTime);
            const motionTransform =
              `translate(-50%, -50%) ` +
              `translate(${layerMotion.translateX}%, ${layerMotion.translateY}%) ` +
              `rotate(${layer.rotation ?? 0}deg) scale(${layerMotion.scale})`;

            if (layer.type === 'effect') {
              return (
                <EditorEffectVisual
                  key={layer.id}
                  effect={layer.src || layer.text}
                  color={layer.color}
                  opacity={effectEdgeOpacity(layer.start, layer.end, currentTime, layer.opacity)}
                  className="z-[5]"
                />
              );
            }

            if (isImageLayer(layer)) {
              return (
                <div
                  key={layer.id}
                  data-layer-overlay
                  onPointerDown={beginLayerDrag(layer)}
                  style={{
                    left: `${layer.x}%`,
                    top: `${layer.y}%`,
                    width: `${layer.widthPercent ?? 26}%`,
                    opacity: layer.opacity * layerMotion.opacityFactor,
                    clipPath: layerMotion.clipPath,
                    filter: layerMotion.filter,
                    // Translate moved off the Tailwind utility classes and
                    // into the inline transform so it composes with rotation
                    // (Tailwind's `-translate-x-1/2 -translate-y-1/2` would
                    // otherwise fight a separate `rotate()` — see EditorPreview
                    // R7 notes). Both functions share the box's own center as
                    // transform-origin, so the anchor point stays at (x%, y%)
                    // regardless of the rotation angle.
                    transform: motionTransform,
                  }}
                  className={cn(
                    'absolute z-10 touch-none select-none',
                    isSelected && 'ring-1 ring-brand-400 ring-offset-2 ring-offset-transparent'
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={layer.src}
                    alt={layer.text}
                    draggable={false}
                    className="h-auto w-full cursor-move object-contain drop-shadow-[0_12px_32px_rgba(0,0,0,0.35)]"
                  />
                  {isSelected && <ResizeGrabbers onResizeStart={(mode) => beginLayerResize(layer, mode)} />}
                  {isSelected && <RotateHandle onRotateStart={beginLayerRotate(layer)} />}
                </div>
              );
            }

            // text (and stickers without a src)
            const isEditingThisLayer = editingLayerId === layer.id;
            return (
              <div
                key={layer.id}
                data-layer-overlay
                onPointerDown={isEditingThisLayer ? undefined : beginLayerDrag(layer)}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  // Already editing this layer: let a second dbl-click inside
                  // the editable behave as normal word-select, don't re-arm
                  // the Escape snapshot with in-progress (uncommitted-to-UI)
                  // text.
                  if (isEditingThisLayer) return;
                  selectLayer(layer.id);
                  preEditTextRef.current = layer.text;
                  setEditingLayerId(layer.id);
                }}
                style={{
                  left: `${layer.x}%`,
                  top: `${layer.y}%`,
                  color: layer.type === 'text' ? layer.color : layer.textColor,
                  fontSize: `${Math.max(layer.size * previewScale, 1)}px`,
                  fontFamily: getLayerFontFamily(layer.fontFamily),
                  fontWeight: layer.fontWeight,
                  fontStyle: layer.fontStyle,
                  textDecoration: layer.underline ? 'underline' : 'none',
                  opacity: layer.opacity * layerMotion.opacityFactor,
                  clipPath: layerMotion.clipPath,
                  filter: layerMotion.filter,
                  ...editableBadgeStyle(layer, previewScale),
                  // See the image-layer branch above: translate lives inline
                  // so it composes with rotation instead of fighting a
                  // Tailwind `-translate-x-1/2 -translate-y-1/2` utility.
                  transform: motionTransform,
                }}
                className={cn(
                  'absolute z-10 touch-none select-none whitespace-nowrap text-center font-black leading-none',
                  layer.type !== 'sticker' && 'px-2.5 py-1 drop-shadow-[0_10px_24px_rgba(0,0,0,0.45)]',
                  isEditingThisLayer ? 'cursor-text' : 'cursor-move',
                  isSelected && 'ring-1 ring-brand-400 ring-offset-2 ring-offset-transparent'
                )}
              >
                {isEditingThisLayer ? (
                  <span
                    contentEditable
                    suppressContentEditableWarning
                    ref={(node) => {
                      if (!node) return;
                      // Guard so this init/focus/select-all logic runs exactly
                      // once per real DOM mount, not on every re-render (a new
                      // inline ref-callback identity each render would
                      // otherwise re-fire and clobber the caret while typing).
                      if (node.dataset.editInit === '1') return;
                      node.dataset.editInit = '1';
                      node.textContent = layer.text;
                      node.focus();
                      const selection = typeof window !== 'undefined' ? window.getSelection() : null;
                      if (selection) {
                        const range = document.createRange();
                        range.selectNodeContents(node);
                        selection.removeAllRanges();
                        selection.addRange(range);
                      }
                    }}
                    onInput={(event) => {
                      updateLayer(layer.id, { text: event.currentTarget.textContent ?? '' });
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.blur();
                      } else if (event.key === 'Escape') {
                        event.preventDefault();
                        updateLayer(layer.id, { text: preEditTextRef.current });
                        setEditingLayerId(null);
                      }
                    }}
                    onBlur={() => setEditingLayerId(null)}
                    onPointerDown={(event) => event.stopPropagation()}
                    style={{ cursor: 'text' }}
                    className="inline-block cursor-text select-text whitespace-pre-wrap outline-none"
                  />
                ) : (
                  layer.text
                )}
                {isSelected && !isEditingThisLayer && (
                  <ResizeGrabbers onResizeStart={(mode) => beginLayerResize(layer, mode)} />
                )}
                {isSelected && !isEditingThisLayer && (
                  <RotateHandle onRotateStart={beginLayerRotate(layer)} />
                )}
              </div>
            );
          })}

          {/* Loading / error overlays */}
          {activeClip && !isActiveMainVideoReady && !inTransitionHandoff && !playbackError && (
            <div className="absolute inset-0 flex items-center justify-center bg-ink-950/45 text-white">
              <div className="flex items-center gap-3 rounded-full bg-white/12 px-4 py-2 text-xs font-black backdrop-blur">
                <Loader2 className="h-4 w-4 animate-spin" />
                Загрузка…
              </div>
            </div>
          )}
          {playbackError && (
            <div className="absolute inset-x-4 bottom-4 rounded-xl bg-black/70 px-3 py-2 text-center text-xs font-bold text-white backdrop-blur">
              {playbackError}
            </div>
          )}

          {/* No center play button — play/pause lives in the control bar below. */}
        </div>
      </div>

      {/* Control bar */}
      <div className="shrink-0 border-t border-white/10 bg-white/[0.03] px-4 py-2.5 backdrop-blur">
        {/* Scrubber */}
        <div
          data-editor-scrubber
          onPointerDown={(event) => {
            event.currentTarget.setPointerCapture(event.pointerId);
            seekToClientX(event.clientX, event.currentTarget);
          }}
          onPointerMove={(event) => {
            if (event.buttons !== 1) return;
            seekToClientX(event.clientX, event.currentTarget);
          }}
          className="group mb-2.5 h-1.5 cursor-ew-resize rounded-full bg-white/10"
        >
          <div className="relative h-full rounded-full bg-brand-600" style={{ width: `${playedPercent}%` }}>
            <span className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-white opacity-0 shadow transition group-hover:opacity-100" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Transport */}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => skipBy(-5)}
              disabled={!activeClip}
              aria-label="Назад на 5 секунд"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white disabled:opacity-40"
            >
              <span className="relative flex h-5 w-5 items-center justify-center">
                <RotateCcw className="h-5 w-5" strokeWidth={1.7} />
                <span className="absolute text-[8px] font-bold leading-none">5</span>
              </span>
            </button>
            <button
              type="button"
              onClick={togglePlayback}
              disabled={!activeClip}
              aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-white transition hover:bg-brand-500 disabled:opacity-40"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 translate-x-0.5" fill="currentColor" />}
            </button>
            <button
              type="button"
              onClick={() => skipBy(5)}
              disabled={!activeClip}
              aria-label="Вперёд на 5 секунд"
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white disabled:opacity-40"
            >
              <span className="relative flex h-5 w-5 items-center justify-center">
                <RotateCw className="h-5 w-5" strokeWidth={1.7} />
                <span className="absolute text-[8px] font-bold leading-none">5</span>
              </span>
            </button>
          </div>

          {/* Timecode */}
          <div className="text-xs font-semibold tabular-nums text-white/70">
            {formatTimecode(currentTime)}
            <span className="text-white/30"> / {formatTimecode(duration)}</span>
          </div>

          <div className="flex-1" />

          {/* Volume */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? 'Включить звук' : 'Выключить звук'}
              className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
            >
              {muted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={muted ? 0 : volume}
              onChange={(event) => {
                const next = Number(event.target.value);
                setVolume(next);
                setMuted(next === 0);
              }}
              aria-label="Громкость"
              className="h-1 w-20 cursor-pointer accent-brand-500"
            />
          </div>

          {/* Aspect selector */}
          <div className="flex items-center gap-0.5 rounded-full bg-white/5 p-0.5">
            {ASPECT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setAspectRatio(option, true)}
                className={cn(
                  'rounded-full px-2 py-1 text-[11px] font-black tabular-nums transition',
                  aspectRatio === option ? 'bg-brand-600 text-white' : 'text-white/60 hover:text-white'
                )}
              >
                {option}
              </button>
            ))}
          </div>

          {/* Fullscreen */}
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label="Полный экран"
            className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Resize grabbers (ported from the monolith's ResizeHandle/ResizeHandles)
// ---------------------------------------------------------------------------

const HANDLE_CLASS: Record<ResizeMode, string> = {
  nw: '-top-1.5 -left-1.5 cursor-nwse-resize',
  ne: '-top-1.5 -right-1.5 cursor-nesw-resize',
  sw: '-bottom-1.5 -left-1.5 cursor-nesw-resize',
  se: '-bottom-1.5 -right-1.5 cursor-nwse-resize',
};

/**
 * Canva-style selection handles: four small square dots at the box corners,
 * sitting just OUTSIDE the bounding box so they never cover the text (the old
 * full-length edge bars swallowed small text). Each corner scales uniformly.
 */
function ResizeGrabbers({
  onResizeStart,
}: {
  onResizeStart: (mode: ResizeMode) => (event: ReactPointerEvent<HTMLElement>) => void;
}) {
  return (
    <>
      {(['nw', 'ne', 'sw', 'se'] as ResizeMode[]).map((mode) => (
        <span
          key={mode}
          role="presentation"
          onPointerDown={onResizeStart(mode)}
          className={cn(
            'absolute z-20 h-2.5 w-2.5 rounded-[3px] border-2 border-brand-500 bg-white shadow-[0_1px_5px_rgba(0,0,0,0.45)]',
            HANDLE_CLASS[mode]
          )}
        />
      ))}
    </>
  );
}

/**
 * Canva-style rotate handle: a small round dot centered above the box's top
 * edge, joined to it by a thin connector line. Lives INSIDE the same
 * rotated/translated overlay div as the box content and the resize grabbers,
 * so it rotates along with the box — the simplest option and the one Canva
 * itself uses (the handle doesn't stay upright while dragging).
 */
function RotateHandle({
  onRotateStart,
}: {
  onRotateStart: (event: ReactPointerEvent<HTMLElement>) => void;
}) {
  return (
    <div
      role="presentation"
      className="absolute left-1/2 top-0 z-20 flex h-11 -translate-x-1/2 -translate-y-full flex-col items-center"
    >
      <button
        type="button"
        aria-label="Повернуть слой"
        onPointerDown={onRotateStart}
        className="flex h-5 w-5 shrink-0 cursor-grab touch-none items-center justify-center rounded-full border-2 border-brand-500 bg-white text-brand-600 shadow-[0_1px_5px_rgba(0,0,0,0.45)] active:cursor-grabbing"
      >
        <RotateCw className="h-3 w-3" strokeWidth={2.2} />
      </button>
      <span className="w-px flex-1 bg-brand-400/70" />
    </div>
  );
}
